import Favori from 'interfaces/Favori'
import { useAppDispatch, useAppSelector } from 'redux/hooks'
import {
  loading,
  succeeded,
  failed,
  checkAllFavoris,
  uncheckAllFavoris,
  Status,
  selectFavori as selectFavoriAction,
  scrollFavoriToTop,
} from 'redux/features/favoris/slice'
import {
  makeSelectStatusExportFile,
  makeSelectErrorExportFile,
  makeSelectFavoris,
  makeSelectSelected,
  makeSelectStatus,
  makeSelectError,
  makeSelectStatusDeleteFavoris,
} from 'redux/features/favoris/selectors'
import { fetchWithCheckAuthorization } from 'utils/fetchWithAuthorization'
import { useCallback, useEffect } from 'react'
import { calculBounds } from 'utils/calculBounds'
import geoJsonObjectToCoordinates from 'utils/geoJsonObjectToCoordinates'
import useGoogleMap from './useGoogleMap'
import useParcelle from './useParcelle'
import { errorParcelle as errorParcelleAction } from '../redux/features/parcelle/slice'

interface ReturnProps {
  favoris: Favori[]
  status: Status
  error: string | null
  exportLoading: boolean
  exportError: string | null
  uncheckAll: () => void
  checkAll: () => void
  isSelected: (uid: string) => boolean
  selectFavori: (uid: string) => void
  countFavorisSaved: number
  countFavorisChecked: number
}

const useFetchFavoris = (): ReturnProps => {
  const favoris = useAppSelector(makeSelectFavoris)
  const selected = useAppSelector(makeSelectSelected)
  const status = useAppSelector(makeSelectStatus)
  const error = useAppSelector(makeSelectError)
  const statusExportFile = useAppSelector(makeSelectStatusExportFile)
  const errorExportFile = useAppSelector(makeSelectErrorExportFile)
  const statusDeleteFavoris = useAppSelector(makeSelectStatusDeleteFavoris)
  const map = useGoogleMap()
  const dispatch = useAppDispatch()
  const { fetchParcelleAddress } = useParcelle()

  const uncheckAll = () => dispatch(uncheckAllFavoris())

  const checkAll = () => dispatch(checkAllFavoris())

  const getFavoris = useCallback(async () => {
    const populateAdressParcelleIntoParcelleJson = async (
      favoriJson: Favori[]
    ) => {
      try {
        let results = favoriJson
        const parcellesWithoutAddress = favoriJson.filter(
          ({ adresseParcelle }: Favori) => adresseParcelle === null
        )
        if (parcellesWithoutAddress.length > 0) {
          const response = await fetchParcelleAddress(
            parcellesWithoutAddress.map(
              ({ uidParcelle, parcelleCentre }: Favori) => ({
                id: uidParcelle,
                lat: parcelleCentre.latitude,
                lng: parcelleCentre.longitude,
              })
            )
          )
          if (response !== null) {
            results = favoriJson.map((parcelle: Favori) => {
              const { adresseParcelle, uidParcelle } = parcelle
              if (adresseParcelle === null) {
                const newAdress = response.find(({ id }) => id === uidParcelle)
                return {
                  ...parcelle,
                  adresseParcelle: newAdress ? newAdress.adresse : '',
                }
              }
              return parcelle
            })
          }
        }
        return results
      } catch (err: unknown) {
        console.error(err)
        dispatch(errorParcelleAction((err as Error).toString()))
        return null
      }
    }
    try {
      dispatch(loading())
      const response = await fetchWithCheckAuthorization('/api/favoris')
      if (response.ok && response.status === 200) {
        const json = await response.json()

        const parcelleWithAddress =
          await populateAdressParcelleIntoParcelleJson(json)

        dispatch(succeeded(parcelleWithAddress))
        return
      }
      switch (response.status) {
        case 401:
          throw Error(response.statusText)
        case 500:
          throw Error('Erreur interne')
        default:
          throw Error('Une erreur est survenue')
      }
    } catch (err: unknown) {
      console.error(err)
      dispatch(failed((err as Error).toString()))
    }
    // La dépendance à fetchParcelleAddress ne pose pas de soucis -> Validé par un dev front
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  useEffect(() => {
    getFavoris()
  }, [getFavoris])

  const isSelected = (uid: string) => (selected && selected === uid) || false

  const selectFavori = (uid: string) => {
    const bounds = calculBounds(
      geoJsonObjectToCoordinates(favoris[uid].parcelleGeography)
    )
    map?.fitBounds(bounds)
    map?.panToBounds(bounds)
    dispatch(selectFavoriAction(uid))
    dispatch(scrollFavoriToTop(uid))
  }
  useEffect(() => {
    if (statusDeleteFavoris === Status.Succeeded) {
      getFavoris()
    }
  }, [statusDeleteFavoris, getFavoris])

  return {
    favoris: Object.values(favoris),
    exportLoading: statusExportFile === Status.Pending,
    exportError: errorExportFile,
    status,
    error,
    uncheckAll,
    checkAll,
    isSelected,
    selectFavori,
    countFavorisSaved: Object.keys(favoris).length,
    countFavorisChecked: Object.values(favoris).filter(
      ({ isChecked }) => isChecked
    ).length,
  }
}

export default useFetchFavoris
